import React, {useState} from "react"
// MUI
import {Box, Grid2 as Grid, Slider, Stack, Switch, Typography} from "@mui/material"
// UI
import {ColorPicker, ColorPickerAlpha, DropzoneImage} from "@/components/UI"
// Data
import {useData} from "@/context/PackagingDataProvider"
// Tokens
import {packagingTokens as tk} from "@/theme/packagingTokens"

// Small uppercase sub-section label (Rémi eyebrow).
function Eyebrow({children}: {children: React.ReactNode}) {
    return (
        <Typography sx={{fontSize: "11px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: tk.color.textTertiary, mb: 1}}>
            {children}
        </Typography>
    )
}

function SubSection({children, last}: {children: React.ReactNode; last?: boolean}) {
    return (
        <Box sx={{pb: last ? 0 : 2.5, mb: last ? 0 : 2.5, borderBottom: last ? "none" : `1px solid ${tk.color.borderLight}`}}>
            {children}
        </Box>
    )
}

// Design settings: logo, background (color or image), consent panel, buttons.
export default function AppEditorDesignSection(props: any) {

    const {app, setApp, t} = props
    const {handleDropImage} = useData()

    // Background editing mode — derived from the current layout on first render.
    const [bgMode, setBgMode] = useState<"color" | "image">(app?.layout?.background?.url ? "image" : "color")

    const handleBackgroundImage = (dataDrop: any) => {
        handleDropImage(dataDrop).then((res: any) => {
            setApp({...app, layout: {...app.layout, background: {url: res.url, color: undefined}}})
        })
    }

    const handleBackgroundColor = (color: string, alpha: number) => {
        setApp({...app, layout: {...app.layout, background: {url: undefined, color: color, alpha: alpha}}})
    }

    const handleLogo = (dataDrop: any) => {
        handleDropImage(dataDrop).then((res: any) => {
            setApp({...app, layout: {...app.layout, logo_url: res.url}})
        })
    }

    const setPanel = (key: string, value: any) =>
        setApp({...app, layout: {...app.layout, panel: {...app.layout.panel, [key]: value}}})

    const handleButtonOptions = (button: any, key: any, value: any) => {
        const _button = {...app.layout.panel[button], [key]: value}
        setApp({...app, layout: {...app.layout, panel: {...app.layout.panel, [button]: _button}}})
    }

    if (!app?.layout) return <></>

    const modePill = (mode: "color" | "image", label: string) => {
        const active = bgMode === mode
        return (
            <Box
                component={"button"}
                type={"button"}
                onClick={() => setBgMode(mode)}
                sx={(theme) => ({
                    cursor: "pointer",
                    px: 1.5, py: "4px",
                    fontSize: "12.5px", fontWeight: 600,
                    fontFamily: "inherit",
                    borderRadius: tk.radius.pill,
                    border: `1px solid ${active ? theme.palette.primary.main : tk.color.border}`,
                    color: active ? theme.palette.primary.main : tk.color.textSecondary,
                    backgroundColor: active ? tk.color.green50 : tk.color.surface,
                    transition: "all .15s",
                })}
            >
                {label}
            </Box>
        )
    }

    return (
        <Box>
            {/* Logo */}
            <SubSection>
                <Eyebrow>{t("app_logo")}</Eyebrow>
                <Stack direction={{xs: "column", sm: "row"}} spacing={2} alignItems={{sm: "center"}}>
                    {app.layout.logo_url && (
                        <Box
                            sx={{
                                width: 84, height: 84, flexShrink: 0,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                border: `1px solid ${tk.color.borderLight}`,
                                borderRadius: tk.radius.card,
                                backgroundColor: tk.color.bg,
                                overflow: "hidden",
                            }}
                        >
                            <img src={app.layout.logo_url} alt={"app logo"} style={{maxWidth: "88%", maxHeight: "88%"}} />
                        </Box>
                    )}
                    <Box sx={{flex: 1}}>
                        <DropzoneImage onDrop={handleLogo} {...{t}} />
                    </Box>
                </Stack>
            </SubSection>

            {/* Background */}
            <SubSection>
                <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1}}>
                    <Eyebrow>{t("app_background")}</Eyebrow>
                    <Stack direction={"row"} spacing={0.75}>
                        {modePill("color", t("bg_mode_color"))}
                        {modePill("image", t("bg_mode_image"))}
                    </Stack>
                </Box>
                {bgMode === "color" ? (
                    <ColorPickerAlpha
                        buttonLabel={t("Color")}
                        buttonStyle={{textTransform: "none"}}
                        buttonSize={"small"}
                        onChangeComplete={handleBackgroundColor}
                        color={app?.layout?.background?.color}
                        alpha={app?.layout?.background?.alpha}
                        {...{t}}
                    />
                ) : (
                    <Stack direction={{xs: "column", sm: "row"}} spacing={2} alignItems={{sm: "center"}}>
                        {app.layout.background?.url && (
                            <Box
                                sx={{
                                    width: 84, height: 84, flexShrink: 0,
                                    border: `1px solid ${tk.color.borderLight}`,
                                    borderRadius: tk.radius.card,
                                    backgroundImage: `url(${app.layout.background.url})`,
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                }}
                            />
                        )}
                        <Box sx={{flex: 1}}>
                            <DropzoneImage onDrop={handleBackgroundImage} {...{t}} />
                        </Box>
                    </Stack>
                )}
            </SubSection>

            {/* Consent panel */}
            <SubSection>
                <Eyebrow>{t("app_popup")}</Eyebrow>
                <Grid container spacing={2}>
                    <Grid size={{xs: 6, sm: 3}}>
                        <ColorPicker
                            {...{t}} noMargin
                            label={t("border_color")}
                            color={app?.layout?.panel?.border_color}
                            setColor={(val: any) => setPanel("border_color", val)}
                        />
                    </Grid>
                    <Grid size={{xs: 6, sm: 3}}>
                        <ColorPicker
                            {...{t}} noMargin
                            label={t("background_color")}
                            color={app?.layout?.panel?.background_color}
                            setColor={(val: any) => setPanel("background_color", val)}
                        />
                    </Grid>
                    <Grid size={{xs: 6, sm: 3}}>
                        <ColorPicker
                            {...{t}} noMargin
                            label={t("text_color")}
                            color={app?.layout?.panel?.text_color}
                            setColor={(val: any) => setPanel("text_color", val)}
                        />
                    </Grid>
                    <Grid size={{xs: 6, sm: 3}}>
                        <Typography sx={{fontSize: "13px", fontWeight: 500, mb: 0.5}}>{t("border_radius")}</Typography>
                        <Slider
                            size={"small"}
                            valueLabelDisplay={"auto"}
                            value={app?.layout?.panel?.rounded || 0}
                            step={1} min={0} max={25}
                            onChange={(e: any) => setPanel("rounded", e.target.value)}
                        />
                    </Grid>
                </Grid>
            </SubSection>

            {/* Buttons — primary / secondary side by side */}
            <SubSection last>
                <Eyebrow>{t("app_buttons")}</Eyebrow>
                <Grid container spacing={2}>
                    {["button_primary", "button_secondary"].map((button: any) => (
                        <Grid size={{xs: 12, md: 6}} key={button}>
                            <Box sx={{border: `1px solid ${tk.color.borderLight}`, borderRadius: tk.radius.card, p: 1.75}}>
                                <Typography sx={{fontSize: "13px", fontWeight: 600, mb: 1.5}}>{t(button)}</Typography>
                                <Grid container spacing={1.5}>
                                    <Grid size={6}>
                                        <ColorPicker
                                            {...{t}} noMargin
                                            label={t("color")}
                                            color={app?.layout?.panel?.[button]?.color}
                                            setColor={(val: any) => handleButtonOptions(button, "color", val)}
                                        />
                                    </Grid>
                                    <Grid size={6}>
                                        <ColorPicker
                                            {...{t}} noMargin
                                            label={t("text_color")}
                                            color={app?.layout?.panel?.[button]?.text_color}
                                            setColor={(val: any) => handleButtonOptions(button, "text_color", val)}
                                        />
                                    </Grid>
                                    <Grid size={6}>
                                        <Typography sx={{fontSize: "13px", fontWeight: 500, mb: 0.5}}>{t("border_radius")}</Typography>
                                        <Slider
                                            size={"small"}
                                            valueLabelDisplay={"auto"}
                                            value={app?.layout?.panel?.[button]?.rounded || 0}
                                            step={1} min={0} max={5}
                                            onChange={(e: any) => handleButtonOptions(button, "rounded", e.target.value)}
                                        />
                                    </Grid>
                                    <Grid size={6} sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                                        <Switch
                                            size={"small"}
                                            checked={!!app?.layout?.panel?.[button]?.outline}
                                            onChange={() => handleButtonOptions(button, "outline", !app?.layout?.panel?.[button]?.outline)}
                                            inputProps={{"aria-label": `${t(button)} ${t("outlined")}`}}
                                        />
                                        <Typography sx={{fontSize: "13px", fontWeight: 500}}>{t("outlined")}</Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </SubSection>
        </Box>
    )
}
